import { Dispatch, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EllipsisMenuButton,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Select,
  SelectContent,
  SelectTrigger,
  Spinner,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import {
  IconCircleQuestion,
  IconPenToSquare,
  IconPlus,
  IconTrashCan,
} from '@optimizely/axiom-icons';

import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useInstanceId } from '@frontend/hooks';
import type { EvaluationSample, EvaluationSampleCreate, SpecializedAgent } from '@frontend/types';

import {
  useCreateEvaluationSample,
  useDeleteEvaluationSample,
  useGetEvaluationSamples,
  useUpdateEvaluationSample,
} from '../../../../hooks/useEvaluationSamples';

import { styles } from './AgentOutputExamples.css';
import { EvaluationSampleForm } from './EvaluationSampleForm';

type AgentOutputExamplesProps = {
  agentId: string;
  agent: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
};

export function AgentOutputExamples({ agentId, agent, dispatch }: AgentOutputExamplesProps) {
  const { instanceId } = useInstanceId();
  const [isFormOpen, setFormOpen] = useState(false);
  const [editingSample, setEditingSample] = useState<EvaluationSample | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState<EvaluationSample | null>(null);

  const { data: samples = [], isLoading } = useGetEvaluationSamples(
    instanceId ?? '',
    agentId,
    !!instanceId && !!agentId
  );
  const createMutation = useCreateEvaluationSample(instanceId ?? '', agentId);
  const updateMutation = useUpdateEvaluationSample(instanceId ?? '', agentId);
  const deleteMutation = useDeleteEvaluationSample(instanceId ?? '', agentId);

  const handleAddClick = () => {
    setEditingSample(undefined);
    setFormOpen(true);
  };

  const handleEditClick = (sample: EvaluationSample) => {
    setEditingSample(sample);
    setFormOpen(true);
  };

  const handleSave = (data: EvaluationSampleCreate) => {
    if (editingSample) {
      // Update existing sample
      updateMutation.mutate(
        { sampleId: editingSample.id, data },
        {
          onSuccess: () => {
            setFormOpen(false);
            setEditingSample(undefined);
          },
        }
      );
    } else {
      // Create new sample
      createMutation.mutate(data, {
        onSuccess: () => {
          setFormOpen(false);
          setEditingSample(undefined);
        },
      });
    }
  };

  const handleDeleteClick = (sample: EvaluationSample) => {
    setSampleToDelete(sample);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sampleToDelete) {
      deleteMutation.mutate(sampleToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSampleToDelete(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSampleToDelete(null);
  };

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="24">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16" w="full">
      <Flex flexDirection="column" gap="8" w="full">
        <Text fontSize="md" color="fg.default">
          Add your preferred outputs (up to 5) to help Opal evaluate its responses.
        </Text>
        <Button
          appearance="default"
          size="md"
          icon={<IconPlus size="16" />}
          onClick={handleAddClick}
          disabled={samples.length >= 5}
          alignSelf="end"
        >
          Add Example
        </Button>
      </Flex>

      {samples.length === 0 ? null : (
        <Flex flexDirection="column" gap="12">
          {samples.map(sample => (
            <Flex key={sample.id} className={styles.sampleCard} flexDirection="row" gap="8">
              <Flex flexDirection="column" gap="8" style={{ flex: 1 }}>
                <Text fontWeight="500">{sample.name}</Text>
                {sample.description && (
                  <Text fontSize="sm" color="fg.secondary">
                    {sample.description}
                  </Text>
                )}
              </Flex>
              <Menu
                options={[
                  {
                    label: 'Edit',
                    intent: 'neutral',
                    addon: <IconPenToSquare size={16} />,
                    execute: () => handleEditClick(sample),
                  },
                  {
                    label: 'Delete',
                    intent: 'danger',
                    addon: <IconTrashCan size={16} />,
                    execute: () => handleDeleteClick(sample),
                  },
                ]}
              >
                <MenuTrigger asChild>
                  <EllipsisMenuButton appearance="subtle" aria-label="Actions" />
                </MenuTrigger>
                <MenuContent />
              </Menu>
            </Flex>
          ))}
        </Flex>
      )}

      {samples.length > 0 && (
        <Flex flexDirection="column" gap="8" w="full">
          <Flex flexDirection="row" gap="8" alignItems="center">
            <Text fontSize="md" color="fg.default">
              Set your baseline quality score to inform Opal&apos;s execution.
            </Text>
            <Tooltip content="The lowest acceptable quality percentage for this agent's response. If the agent's output quality falls below this score, Opal will flag it in the execution logs.">
              <IconCircleQuestion size={16} />
            </Tooltip>
          </Flex>
          <Select
            options={Array.from({ length: 10 }, (_, i) => (i + 1) * 10).map(score => ({
              label: `${score}%`,
              value: String(score),
            }))}
            value={agent.baseline_evaluation_score?.toString() ?? '80'}
            onValueChange={value =>
              dispatch({
                type: 'UPDATE_FIELD',
                field: 'baseline_evaluation_score',
                value: value ? Number(value) : undefined,
              })
            }
          >
            <SelectTrigger w="224" />
            <SelectContent />
          </Select>
        </Flex>
      )}

      <EvaluationSampleForm
        initialValues={editingSample}
        onSave={handleSave}
        onCancel={() => {
          setFormOpen(false);
          setEditingSample(undefined);
        }}
        open={isFormOpen}
        onOpenChange={setFormOpen}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>Delete Example</DialogHeader>
          <DialogBody>
            <Text>
              Are you sure you want to delete the example &quot;{sampleToDelete?.name}&quot;? This
              action cannot be undone.
            </Text>
          </DialogBody>
          <DialogFooter>
            <Flex flexDirection="row" gap="8" justifyContent="flex-end" w="full">
              <Button
                appearance="default"
                onClick={handleCancelDelete}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                appearance="danger"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Flex>
  );
}
